/**
 * Note player (node N4) — renders a saved, gated recorded-note attachment wherever
 * attachments show (Room feed, chat, the registrant watch page). A video/screen note
 * plays in the app's real `.w-player` chrome (watch.css — same as the watch page's
 * StreamPlayer and the Recorder preview); a voice note plays in a compact WhatsApp-style
 * waveform bar matching the Recorder's voice review look. Both are Ledger-themed (light +
 * dark), and the `media_url` already carries its `?t=<media_token>` so the private R2
 * object streams through videos.flashsuite.net. A long video note is an adaptive stream
 * (`.m3u8`) and plays through src/lib/noteStream.ts.
 *
 * The URLs are minted on READ by the API (workers/src/notes/enrich.ts); a note attachment
 * with no `media_url` (still transcoding, or an old row) renders nothing here.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Attachment } from "../../shared/types";
import { clockSeconds, useNoteStream } from "../lib/noteStream";
import "../styles/watch.css";
import "../styles/note-player.css";

const IcPlay = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const IcPause = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" />
    <rect x="14" y="5" width="4" height="14" />
  </svg>
);
const IcVol = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} aria-hidden="true">
    <path d="M11 5 6 9H2v6h4l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14" />
  </svg>
);
const IcExpand = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} aria-hidden="true">
    <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M3 16v3a2 2 0 0 0 2 2h3" />
  </svg>
);

type FsVideo = HTMLVideoElement & {
  webkitRequestFullscreen?: () => void;
  webkitEnterFullscreen?: () => void;
};

/** M:SS clock for the player time display. */
function clock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const s = Math.floor(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Public entry — a video note plays in the w-player, a voice note in the waveform bar. */
export function NotePlayer({ attachment }: { attachment: Attachment }): ReactNode {
  if (!attachment.media_url) return null;
  return attachment.kind === "voice" ? (
    <VoiceNotePlayer
      src={attachment.media_url}
      durationLabel={attachment.duration}
      seed={attachment.media_key ?? attachment.name}
    />
  ) : (
    <VideoNotePlayer src={attachment.media_url} poster={attachment.poster_url} durationLabel={attachment.duration} />
  );
}
export default NotePlayer;

// ─────────────────────────────── video note ───────────────────────────────

function VideoNotePlayer({
  src,
  poster,
  durationLabel,
}: {
  src: string;
  poster?: string;
  durationLabel?: string;
}): ReactNode {
  const videoRef = useRef<HTMLVideoElement>(null);
  const stream = useNoteStream(videoRef, src);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [total, setTotal] = useState(0);
  const [muted, setMuted] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
  };
  const onMeta = () => {
    const v = videoRef.current;
    if (v && Number.isFinite(v.duration)) setTotal(v.duration);
  };
  const seek = (clientX: number, el: HTMLElement) => {
    const v = videoRef.current;
    if (!v || !total || !Number.isFinite(total)) return;
    const r = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    v.currentTime = frac * total;
    setCur(frac * total);
  };
  const onScrubDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    seek(e.clientX, el);
    const mv = (ev: PointerEvent) => seek(ev.clientX, el);
    const up = () => {
      window.removeEventListener("pointermove", mv);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const fullscreen = () => {
    const v = videoRef.current as FsVideo | null;
    if (!v) return;
    const f = v.requestFullscreen ?? v.webkitRequestFullscreen ?? v.webkitEnterFullscreen;
    if (f) f.call(v);
  };

  const pct = total > 0 ? Math.max(0, Math.min(100, (cur / total) * 100)) : 0;
  const length = total > 0 ? total : clockSeconds(durationLabel);

  return (
    <div className="fw-np fw-np-video">
      <div className="w-player" style={{ aspectRatio: "16 / 9" }}>
        <video
          ref={videoRef}
          className="w-video"
          src={stream ? undefined : src}
          poster={poster}
          playsInline
          preload="metadata"
          onClick={toggle}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onLoadedMetadata={onMeta}
          onDurationChange={onMeta}
          onTimeUpdate={() => {
            const v = videoRef.current;
            if (v) setCur(v.currentTime);
          }}
        >
          <track kind="captions" />
        </video>
        <button
          type="button"
          className={`w-playbtn${playing ? " w-hidden" : ""}`}
          aria-label="Play"
          onClick={toggle}
        >
          <IcPlay />
        </button>
        <div className="w-controls">
          <button type="button" className="w-mini-play" aria-label="Play or pause" onClick={toggle}>
            {playing ? <IcPause /> : <IcPlay />}
          </button>
          <div
            className="w-scrub"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
            tabIndex={0}
            onPointerDown={onScrubDown}
          >
            <div className="w-fill" style={{ width: `${pct}%` }} />
            <div className="w-knob" style={{ left: `${pct}%` }} />
          </div>
          <span className="w-tc">
            {clock(cur)} / {clock(length)}
          </span>
          <button
            type="button"
            className="w-cbtn"
            title="Mute"
            aria-label="Mute"
            style={{ opacity: muted ? 0.45 : 1 }}
            onClick={toggleMute}
          >
            <IcVol />
          </button>
          <button type="button" className="w-cbtn" title="Fullscreen" aria-label="Fullscreen" onClick={fullscreen}>
            <IcExpand />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────── voice note ───────────────────────────────

const BAR_COUNT = 40;

/** Deterministic bar heights (0.18–1) from a seed, so a note's waveform is stable. */
function barsFor(seed: string): number[] {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const bars: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    const v = ((h >>> 0) % 1000) / 1000;
    bars.push(0.18 + v * 0.82);
  }
  return bars;
}

/** The voice bar on its own: local previews (a just-recorded take, a picked file)
 *  get the same play/pause + waveform seek as a saved note. */
export function VoiceNotePlayer({
  src,
  durationLabel,
  seed,
}: {
  src: string;
  durationLabel?: string;
  seed: string;
}): ReactNode {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barsRef = useRef<number[]>(barsFor(seed));
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = "metadata";
    audioRef.current = audio;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setCur(0);
    };
    const onTime = () => setCur(audio.currentTime);
    const onMeta = () => {
      if (Number.isFinite(audio.duration)) setTotal(audio.duration);
    };
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    return () => {
      audio.pause();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audioRef.current = null;
    };
  }, [src]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  }, []);

  const seek = (clientX: number, el: HTMLElement) => {
    const a = audioRef.current;
    if (!a || !total || !Number.isFinite(total)) return;
    const r = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    a.currentTime = frac * total;
    setCur(frac * total);
  };

  const played = total > 0 ? cur / total : 0;
  const timeText = total > 0 ? clock(total - cur) : (durationLabel ?? "0:00");

  return (
    <div className="fw-np fw-np-voice">
      <button
        type="button"
        className="fw-np-play"
        aria-label={playing ? "Pause voice note" : "Play voice note"}
        onClick={toggle}
      >
        {playing ? <IcPause /> : <IcPlay />}
      </button>
      <button
        type="button"
        className="fw-np-wave"
        aria-label="Seek voice note"
        onPointerDown={(e) => seek(e.clientX, e.currentTarget)}
      >
        {barsRef.current.map((v, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static waveform
            key={i}
            className={`fw-np-bar${i / BAR_COUNT < played ? " fw-np-on" : ""}`}
            style={{ height: `${Math.round(v * 100)}%` }}
          />
        ))}
      </button>
      <span className="fw-np-dur fw-np-num">{timeText}</span>
    </div>
  );
}
